import { Accordion, Button, Field } from "../components/rumonai";
import { LightMotion } from "../components/LightMotion";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { ErrorState } from "../shell/ui";
import { PasswordField } from "./AuthShell";
import { hub, type EmailState } from "./api";
import { useAutoRefresh } from "./autoRefresh";
import "../connections/settings-list.css";

/** What a Hub with no mail service says wherever a mail was to be sent; the same sentence `hub/recovery_app.py` answers with. */
const NO_MAIL = "平台管理员尚未开通邮件服务，确认邮件暂时发不出去。请把下方故障说明交给邀请你使用涟漪的管理员。";

/**
 * 设置 › 账号: the address a forgotten password is recovered by.
 *
 * An address is only used once its owner has opened the link mailed to it, so
 * this row says which of the two it is holding -- the confirmed one, and one
 * still waiting -- in two lines at most, and never lets a mail that did not go
 * pass as one that did: what the last attempt came to is the waiting address's
 * own line, not a third.
 * Changing it asks for the password: where a reset link goes is as serious as
 * the password itself. The confirmed address keeps working until the new one is
 * confirmed.
 */
export function AccountEmail({ accountName }: { accountName: string }) {
  const [state, setState] = useState<EmailState | null>(null);
  const [readError, setReadError] = useState<unknown>(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [current, setCurrent] = useState("");
  const [passwordHidden, hidePassword] = useState(true);
  const emailField = useRef<HTMLInputElement>(null);
  const ready = Boolean(email.trim() && current && emailField.current?.validity.valid);
  const [busy, setBusy] = useState<"save" | "resend" | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [copyNote, setCopyNote] = useState("");
  const [troubleshooting, setTroubleshooting] = useState<string[]>([]);
  /** What the last mail attempt came to: the address it went to, or why it did not go. */
  const [outcome, setOutcome] = useState<{ sent: string | null; note: string | null } | null>(null);
  useEffect(() => {
    const abort = new AbortController();
    hub.email().then(
      value => { if (!abort.signal.aborted) setState(value); },
      failure => { if (!abort.signal.aborted) setReadError(failure); },
    );
    return () => abort.abort();
  }, []);
  // The address is confirmed in a mail, in another tab: when this window is back in front it says so. A read that
  // fails here leaves what is on screen alone.
  useAutoRefresh(useCallback(() => {
    hub.email().then(value => setState(value), () => {});
  }, []));
  const retryRead = () => {
    setReadError(null);
    hub.email().then(value => setState(value), failure => setReadError(failure));
  };
  const close = () => {
    setOpen(false);
    setEmail("");
    setCurrent("");
    hidePassword(true);
    setError(null);
  };
  const report = (value: EmailState) => {
    setState(value);
    setOutcome({ sent: value.note ? null : value.pending, note: value.note ?? null });
  };
  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy || !email.trim() || !current || !event.currentTarget.checkValidity()) return;
    setBusy("save");
    setError(null);
    try {
      report(await hub.setEmail(email.trim(), current));
      close();
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(null);
    }
  };
  const resend = async () => {
    if (busy) return;
    setBusy("resend");
    setError(null);
    try {
      report(await hub.resendConfirmation());
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(null);
    }
  };
  const waiting = state?.pending ?? null;
  // Nothing here promises a mail the Hub cannot send.
  const mail = state?.mail_ready !== false;
  return <section className="settings-block workspace-setting-row account-password account-email" aria-labelledby="account-email-title">
    <div>
      <h3 id="account-email-title">邮箱</h3>
      {readError ? <p className="settings-notice">暂时没有读到邮箱。<Button variant="ghost" onClick={retryRead}>重试</Button></p>
        : !state ? <p>正在读取…</p>
        : <>
          {state.email ? <p className={mail ? undefined : "settings-notice"}><span data-quoted="true">{state.email}</span> 已确认。{mail
              ? "忘记密码时，重置链接会发到这里。" : "邮件服务还没有开通，现在发不出重置链接。"}</p>
            : waiting ? null : <p className={mail ? undefined : "settings-notice"}>还没有邮箱。{mail
              ? "添加并确认后，忘记密码可以自己找回。" : "邮件服务还没有开通，添加了也确认不了。"}</p>}
          {waiting ? <p className={`settings-notice${outcome ? " account-email-outcome pa-feedback" : ""}`} role={outcome ? "status" : undefined}>
            <span data-quoted="true">{waiting}</span> 还没有确认，{outcome?.note ? outcome.note
              : !mail ? NO_MAIL
              : outcome?.sent ? "确认邮件刚发出，打开里面的链接就好。" : "打开确认邮件里的链接就好。"}</p> : null}
          {!waiting && outcome?.note ? <p className="settings-notice account-email-outcome pa-feedback" role="status">{outcome.note}</p> : null}
        </>}
    </div>
    {state?.mail_ready === false ? <div className="settings-block-wide">
      <Accordion value={troubleshooting} onValueChange={setTroubleshooting} items={[{
        value: "mail", title: "邮件问题的处理方法", content: <>
          <p>请平台管理员打开管理后台的“邮件服务”，配置后发送测试邮件；成功后回到这里重试。邮箱变更仍需你本人验证。</p>
          <Button variant="secondary" onClick={() => { void navigator.clipboard.writeText("涟漪邮件服务尚未开通。请管理员在管理后台配置邮件服务并发送测试邮件，然后通知我重试邮箱验证。").then(() => setCopyNote("已复制，可交给平台管理员。"), () => setCopyNote("复制没有成功，请选中上方处理方法手动复制。")); }}>复制故障说明</Button>
          {copyNote ? <p role="status">{copyNote}</p> : null}
        </>,
      }]} />
    </div> : null}
    {state && !open ? <div className="account-password-actions">
      <Button variant="secondary" name="change-email" disabled={Boolean(busy)}
        onClick={() => { setOpen(true); setOutcome(null); }}>{state.email || waiting ? "更换邮箱" : "添加邮箱"}</Button>
      {waiting && mail ? <Button variant="secondary" name="resend-email" disabled={Boolean(busy)}
        aria-busy={busy === "resend"} onClick={() => { void resend(); }}>
        {busy === "resend" ? <><LightMotion state="working" compact />正在发送…</> : "重新发送"}</Button> : null}
    </div> : null}
    {error && !open ? <ErrorState error={error} /> : null}
    {open ? <form onSubmit={event => { void save(event); }} aria-busy={busy === "save"}>
      {/* Give password managers the signed-in identity; the recovery address is a separate field. */}
      <input type="text" name="username" autoComplete="username" value={accountName} readOnly hidden />
      <Field label="邮箱">
        <input ref={emailField} type="email" name="email" autoComplete="email" inputMode="email" spellCheck={false}
          placeholder="name@example.com" value={email} onChange={event => setEmail(event.target.value)}
          required disabled={busy === "save"} autoFocus />
      </Field>
      <PasswordField label="当前密码" name="current-password" placeholder="当前账号使用的密码"
        hint={"请使用当前账号「" + accountName + "」的密码。"} autoComplete="current-password"
        value={current} onChange={setCurrent} setting={false} disabled={busy === "save"}
        hidden={passwordHidden} onHide={hidePassword} />
      {error ? <ErrorState error={error} /> : null}
      <div className="account-password-actions">
        <Button type="submit" disabled={busy === "save" || !ready}
          aria-busy={busy === "save"}>
          {busy === "save" ? <><LightMotion state="working" compact />{mail ? "正在发送…" : "正在保存…"}</> : mail ? "发送确认邮件" : "保存邮箱"}
        </Button>
        <Button variant="ghost" disabled={busy === "save"} onClick={close}>取消</Button>
      </div>
    </form> : null}
  </section>;
}
